import Service from '@ember/service';
import Store from '@ember-data/store';
import PubSub from 'pubsub-js';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import CampaignModel from 'donor-elf-web/models/campaign';
import HostCampaignModel from 'donor-elf-web/models/host-campaign';
import FundsService from './funds-service';
import Settings from './settings';
import {
  indexArrayByPropertyName,
  sortByProperties,
} from 'donor-elf-web/lib/array-utils';
import ExpiringMemoryCache from './expiring-memory-cache';
import CampaignContactModel from 'donor-elf-web/models/campaign-contact';
import { queryWithPages } from 'donor-elf-web/lib/store-utils';
import AddSomethingModal, {
  type ParentCampaignChangedArgs,
  type CampaignSavedArgs,
  type CampaignGoalSavedArgs,
  type CampaignDeletedArgs,
} from './add-something-modal';
import type { SelectOption } from 'donor-elf-web/lib/form-component-utils';
import CampaignGoalModel from 'donor-elf-web/models/campaign-goal';

const CAMPAIGN_CONTACTS_CHANGED_TOPIC = 'campaign-contacts-changed';

export default class CampaignService extends Service {
  @service declare addSomethingModal: AddSomethingModal;
  @service declare expiringMemoryCache: ExpiringMemoryCache;
  @service declare fundsService: FundsService;
  @service declare settings: Settings;
  @service declare store: Store;

  @tracked campaigns: CampaignModel[] = [];
  @tracked hostCampaigns: HostCampaignModel[] = [];
  @tracked campaignContacts: CampaignContactModel[] = [];
  @tracked activeCampaignGoals: CampaignGoalModel[] = [];

  campaignGoalSavedSubsciptionToken?: string;

  get campaignsEnabled() {
    return this.settings.fundSettings?.campaignsEnabled;
  }

  @cached
  get campaignsById() {
    let byId: { [campaignId: string]: CampaignModel } = {};
    for (let campaign of this.campaigns) {
      byId[campaign.id] = campaign;
    }
    return byId;
  }

  @cached
  get hostCampaignsById() {
    let byId: { [hostCampaignId: string]: HostCampaignModel } = {};
    for (let hostCampaign of this.hostCampaigns) {
      byId[hostCampaign.id] = hostCampaign;
    }
    return byId;
  }

  @cached
  get sortedCampaigns() {
    return sortByProperties(this.campaigns, ['restricted:asc', 'label:asc']);
  }

  @cached
  get sortedNonRestrictedCampaigns() {
    return this.sortedCampaigns.filter((c) => !c.restricted);
  }

  @cached
  get sortedCampaignSelectOptions(): SelectOption[] {
    return this.sortedCampaigns.map((campaign) => {
      return { val: campaign.id, label: campaign.label };
    });
  }

  @cached
  get activeCampaignGoalsByCampaignId() {
    return indexArrayByPropertyName(this.activeCampaignGoals, 'campaignId');
  }

  @cached
  get campaignContactsByContactId(): {
    [contactId: string]: CampaignContactModel[];
  } {
    let byId: any = {};
    for (let x of this.campaignContacts) {
      if (!byId[x.contactId]) {
        byId[x.contactId] = [];
      }
      byId[x.contactId].push(x);
    }
    return byId;
  }

  _loadPromise?: Promise<void>;

  load({ forceReload }: { forceReload?: boolean } = {}): Promise<void> {
    if (forceReload) {
      this._loadPromise = undefined;
    }
    if (
      this.fundsService.selectedFund?.id === undefined ||
      !this.campaignsEnabled
    ) {
      this.campaigns = [];
      this.hostCampaigns = [];
      this.activeCampaignGoals = [];
      this._loadPromise = Promise.resolve();
    }
    if (this._loadPromise) {
      return this._loadPromise;
    }

    if (this.campaignGoalSavedSubsciptionToken == null) {
      this.campaignGoalSavedSubsciptionToken =
        this.addSomethingModal.subscribeToCampaignGoalSaved(
          this.handleCampaignGoalSaved,
        );
    }

    // intentionally not awaiting this
    this.ensureAllCampaignContactsLoaded();

    return (this._loadPromise = this.store
      .findRecord('fund-campaigns-info', this.fundsService.selectedFund!.id)
      .then((record) => {
        this.campaigns = record.campaigns.slice();
        this.hostCampaigns = record.hostCampaigns.slice();
        this.activeCampaignGoals = record.activeCampaignGoals.slice();
        this.setParentAndChildrenCampaigns();
      }));
  }

  async reloadActiveCampaignGoals() {
    const campaignGoals = (
      await this.store.query('campaign-goal', {
        filter: {
          currently_active: true,
        },
      })
    ).slice();
    this.activeCampaignGoals = campaignGoals;
  }

  // manually call this after the campaign has been saved and this will call
  // addSomethingModal.campaignSaved
  onCampaignSaved = async (data: CampaignSavedArgs) => {
    let { campaign } = data;

    if (data.newCampaign) {
      this.campaigns = [...this.campaigns, campaign];
      this.setParentAndChildrenCampaigns();
      this.addSomethingModal.campaignSaved(data);

      // add the associated campaign goal for the new campaign
      let campaignGoals = (
        await this.store.query('campaign-goal', {
          filter: {
            campaign_id: campaign.id,
            currently_active: true,
          },
        })
      ).slice();
      if (campaignGoals.length === 1) {
        let campaignGoal = campaignGoals[0];
        this.activeCampaignGoals = [...this.activeCampaignGoals, campaignGoal];
        this.addSomethingModal.campaignGoalSaved({
          campaign: data.campaign,
          campaignGoal,
          newCampaignGoal: true,
        });
      }
    } else {
      this.addSomethingModal.campaignSaved(data);
    }
  };

  // manually call this after the parent campaign changed. This will call
  // addSomethingModal.campaignParentCampaignChanged()
  onParentCampaignChanged = async (data: ParentCampaignChangedArgs) => {
    await data.campaign.reload();
    let previousParentCampaign =
      this.campaignsById[data.previousParentCampaignId!];
    if (previousParentCampaign) {
      await previousParentCampaign.reload();
    }
    this.setParentAndChildrenCampaigns();

    // update all the active goals
    let fundCampaignsInfo = await this.store.findRecord(
      'fund-campaigns-info',
      this.fundsService.selectedFund!.id,
    );
    this.activeCampaignGoals = fundCampaignsInfo.activeCampaignGoals.slice();
    this.addSomethingModal.parentCampaignChanged(data);
  };

  // manually call this after a delete campaign event. This will call
  // addSomethingModal.campaignDeleted()
  onCampaignDeleted = async (data: CampaignDeletedArgs) => {
    await this.load({ forceReload: true });
    this.addSomethingModal.campaignDeleted(data);
  };

  handleCampaignGoalSaved = async (
    _msg: string,
    data: CampaignGoalSavedArgs,
  ) => {
    if (data.newCampaignGoal) {
      this.activeCampaignGoals = [
        ...this.activeCampaignGoals,
        data.campaignGoal,
      ];
    }
  };

  async ensureAllCampaignContactsLoaded(): Promise<void> {
    let campaignContactsResult = await this.expiringMemoryCache.fetch<
      CampaignContactModel[]
    >({
      key: 'campaignContacts',
      expiresInMinutes: 10,
      loadValue: () => {
        return queryWithPages<CampaignContactModel>({
          store: this.store,
          modelName: 'campaign-contact',
          queryOptions: {},
          pageSize: 1000,
        });
      },
    });
    if (campaignContactsResult.fromCache) {
      // don't need to update the campaignContacts if they're still cached
      return;
    }
    this.campaignContacts = campaignContactsResult.value;
    PubSub.publish(CAMPAIGN_CONTACTS_CHANGED_TOPIC);
  }

  async reloadCampaignContactsIfContactIdNotInCampaign(args: {
    contactId: string;
    campaignId: string;
  }) {
    if (
      this.campaignContactsByContactId[args.contactId]?.find(
        (x) => x.campaignId === args.campaignId,
      ) == null
    ) {
      this.expiringMemoryCache.remove('campaignContacts');
    }
    return this.ensureAllCampaignContactsLoaded();
  }

  subscribeToCampaignContactsChanged(fn: () => void): string {
    return PubSub.subscribe(CAMPAIGN_CONTACTS_CHANGED_TOPIC, fn);
  }

  setParentAndChildrenCampaigns() {
    let { campaignsById } = this;
    for (let campaign of this.campaigns) {
      let parentCampaign = campaignsById[campaign.parentCampaignId!];
      if (campaign.parentCampaign !== parentCampaign) {
        campaign.parentCampaign = campaignsById[campaign.parentCampaignId!];
      }

      let childrenCampaigns = this.campaigns.filter(
        (x) => x.parentCampaignId === campaign.id,
      );
      if (
        childrenCampaigns.map((x) => x.id).sort() !==
        campaign.childrenCampaigns.map((x) => x.id).sort()
      ) {
        campaign.childrenCampaigns = this.campaigns.filter(
          (x) => x.parentCampaignId === campaign.id,
        );
      }
    }

    for (let campaign of this.campaigns) {
      let ancestors: CampaignModel[] = [];
      let parentCampaign = campaign.parentCampaign;
      while (parentCampaign) {
        ancestors = [...ancestors, parentCampaign];
        parentCampaign = parentCampaign.parentCampaign;
      }
      if (
        campaign.ancestorCampaigns.map((x) => x.id) !==
        ancestors.map((x) => x.id)
      ) {
        campaign.ancestorCampaigns = ancestors;
      }
    }
  }
}
